import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Industries', href: '/industries' },
  { label: 'What We Deliver', href: '/#deliver' },
  { label: 'Why AlphaOps AI', href: '/#why' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      if (location.pathname !== '/') {
        window.location.href = href;
        return;
      }
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1360px] mx-auto px-8 md:px-16 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-[120px] h-[120px] flex items-center justify-center flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/65c3d545-bf67-4e3a-abd4-07a3626b250e.png"
              alt="AlphaOps AI"
              className="w-full h-full object-contain"
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.href.startsWith('/#') ? (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-[#A0A0A0] hover:text-white text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-[#A0A0A0] hover:text-white text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            )
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center">
          <Link
            to="/#cta"
            onClick={() => handleNavClick('/#cta')}
            className="border border-white/30 text-white text-sm px-5 py-2 rounded-full hover:bg-[#38C6F5] hover:text-black hover:border-[#38C6F5] transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Get Started
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A0A0A]/98 border-t border-white/5 px-8 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            link.href.startsWith('/#') ? (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-[#A0A0A0] hover:text-white text-base text-left transition-colors cursor-pointer"
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="text-[#A0A0A0] hover:text-white text-base transition-colors cursor-pointer"
              >
                {link.label}
              </Link>
            )
          ))}
          <Link
            to="/#cta"
            onClick={() => handleNavClick('/#cta')}
            className="border border-white/30 text-white text-sm px-5 py-2 rounded-full text-center hover:bg-[#38C6F5] hover:text-black hover:border-[#38C6F5] transition-all duration-200 cursor-pointer whitespace-nowrap mt-2"
          >
            Get Started
          </Link>
        </div>
      )}
    </header>
  );
}
